import { useQuery } from "react-query";
import { useDispatch, useSelector } from "react-redux";
import { selectSelectedAxis } from "../../Cartesian/store/slices/cartesian";
import { selectConfigurationType } from "../../Common/store/slices/common";
import {
  selectRowsToShow,
  setFetchedBundles,
  setInertiaRatioUserInput,
  setNominalTorqueUserInput,
  setOrderBy,
  setPaging
} from "../../Retrofit/store/slices/retrofit";
import { getBundles, getProductInformations } from "../../Common/utils/api/api";
import { formatConnectorType, formatMotorEncoder, formatNbPhases, formatShaftEnd } from "../utils/func.util";

export const useGetRetrofitBundles = (filters) => {
  const selectedAxis = useSelector(selectSelectedAxis);
  const configurationType = useSelector(selectConfigurationType);
  const dispatch = useDispatch();
  const lang = window.msizerConfiguration?.locale || "en-WW";
  const rowsToShow = useSelector(selectRowsToShow);

  const processBundles = async (bundles) => {
    // get the list of ids of each motor, drive, gearbox and adaptation kits
    let motorIds = [];
    let driveIds = [];
    let gearboxIds = [];
    let adaptationKitIds = [];
    bundles.forEach((singleBundle) => {
      motorIds.push(singleBundle.motor.commercialReference);
      driveIds.push(singleBundle.drive.commercialReference);
      gearboxIds.push(singleBundle.gearbox?.commercialReference);
      adaptationKitIds.push(singleBundle.adaptationKit?.commercialReference);
    });
    //delete doublons
    motorIds = Array.from(new Set(motorIds));
    driveIds = Array.from(new Set(driveIds));
    gearboxIds = Array.from(new Set(gearboxIds));
    adaptationKitIds = Array.from(new Set(adaptationKitIds));
    // call the schneider's api for translation
    const motorInfosTranslated = await getProductInformations(motorIds);
    const driveInfosTranslated = await getProductInformations(driveIds);
    const gearboxInfosTranslated = await getProductInformations(gearboxIds);
    const adaptationKitInfosTranslated = await getProductInformations(adaptationKitIds);
    // add translation informations to bdls object
    bundles.forEach((singleBundle) => {
      // console.log("singleBundle", singleBundle);
      if (singleBundle.motor) {
        singleBundle.motor.translationData = motorInfosTranslated.filter(
          (elt) => elt?.catalogue_product?.id === singleBundle?.motor?.commercialReference
        );
      }
      if (singleBundle.drive) {
        singleBundle.drive.translationData = driveInfosTranslated.filter(
          (elt) => elt?.catalogue_product?.id === singleBundle?.drive?.commercialReference
        );
      }
      if (singleBundle.gearbox) {
        singleBundle.gearbox.translationData = gearboxInfosTranslated?.filter(
          (elt) => elt?.catalogue_product?.id === singleBundle?.gearbox?.commercialReference
        );
      }
      if (singleBundle.adaptationKit) {
        singleBundle.adaptationKit.translationData = adaptationKitInfosTranslated?.filter(
          (elt) => elt?.catalogue_product?.id === singleBundle?.adaptationKit?.commercialReference
        );
      }
    });

    return bundles;
  };

  const processCartesianBundles = async (cartesianBundles) => {
    // get the list of ids of each motor, drive, gearbox and adaptation kits
    const tmpBundles = await processBundles(cartesianBundles.map((b) => b));
    // add the cartesianAdaptationKit to the cartesian bundles
    tmpBundles.forEach((bundle) => {
      cartesianBundles.forEach((cb) => {
        if (cb.bundle.id === bundle.id) {
          bundle.cartesianAdaptationKit = cb.cartesianAdaptationKit;
        }
      });
    });
    return tmpBundles;
  };

  const {
    refetch: fetchRetrofitBundles,
    isLoading,
    isFetching
  } = useQuery(
    "FETCH_BUNDLES",
    async () => {
      // if filters?.driveFilter object is not empty then check if all mandatory filters are filled
      const driveFiltersApplicable = filters?.driveFilter !== undefined && filters?.driveFilter !== null;

      // if filters?.motorFilter object is not empty then check if all mandatory filters are filled
      const motorFiltersApplicable = filters?.motorFilter !== undefined && filters?.motorFilter !== null;

      // if filters?.gearboxFilter object is not empty then check if all mandatory filters are filled
      const gearboxFiltersApplicable = filters?.gearboxFilter !== undefined && filters?.gearboxFilter !== null;

      if (
        !(
          filters &&
          (!driveFiltersApplicable || checkDriveFilters()) &&
          (!motorFiltersApplicable || checkMotorFilters()) &&
          (!gearboxFiltersApplicable || checkGearboxFilters())
        )
      ) {
        return;
      }

      const payload = {
        locale: lang,
        paging: {
          skip: 0,
          limit: 500
        },
        search: {
          applicationDataFilter: {
            supplyVoltage:
              filters?.driveFilter.supplyVoltage?.length > 0 ? filters?.driveFilter.supplyVoltage : undefined,
            nominalTorqueOfMotor: filters?.motorFilter?.nominalTorque,
            nominalSpeedOfMotor: filters?.motorFilter?.nominalSpeed,
            nominalTorque: filters?.applicationDataFilter?.nominalTorque
              ? {
                  min: parseFloat(filters?.applicationDataFilter?.nominalTorque)
                  /*
                  max:
                    parseFloat(filters?.applicationDataFilter?.nominalTorque) *
                    getNominalToqueMultiplicator(configurationType)
                  */
                }
              : undefined,
            // nominalSpeed is named nominalSpeed but actually it's considered as a minimum
            nominalSpeed: filters?.applicationDataFilter?.nominalSpeed
              ? {
                  min: Number(filters?.applicationDataFilter?.nominalSpeed)
                }
              : undefined,
            withGearbox: filters?.motorFilter.withGearbox || filters?.gearboxFilter?.enabled,
            length: filters?.applicationDataFilter?.maxLength
              ? { max: Number(filters?.applicationDataFilter?.maxLength) }
              : undefined,
            withBrake: filters?.motorFilter.holdingBrake.length !== 1 ? undefined : filters?.motorFilter.holdingBrake[0]
          },
          motorFilter: {
            type: filters?.motorFilter.motorType?.length > 0 ? filters?.motorFilter.motorType : undefined,
            nominalTorqueOfMotor: filters?.motorFilter.nominalTorque,
            encoder:
              formatMotorEncoder(filters?.motorFilter.motorEncoder)?.length > 0
                ? formatMotorEncoder(filters?.motorFilter.motorEncoder)
                : undefined,
            connector:
              formatConnectorType(filters?.motorFilter.connectorType)?.length > 0
                ? formatConnectorType(filters?.motorFilter.connectorType)
                : undefined,
            flangeSize: filters?.motorFilter.flangeSize,
            withBrake:
              filters?.motorFilter.holdingBrake.length !== 1 ? undefined : filters?.motorFilter.holdingBrake[0],
            length: filters?.motorFilter.length,
            ipProtections:
              filters?.motorFilter.ipDegreeOfProtection.length > 0
                ? filters?.motorFilter.ipDegreeOfProtection
                : undefined,
            shaftEnd:
              formatShaftEnd(filters?.motorFilter.shaftEnd)?.length > 0
                ? formatShaftEnd(filters?.motorFilter.shaftEnd)
                : undefined,
            peakStallTorque: filters?.motorFilter.peakStallTorque,
            continuousStallTorque: filters?.motorFilter.continuousStallTorque
          },
          driveFilter: {
            type: filters?.driveFilter.driveType?.length > 0 ? filters?.driveFilter.driveType : undefined,
            outputCurrents:
              filters?.driveFilter.outputCurrent.length > 0 ? filters?.driveFilter.outputCurrent : undefined,
            communicationInterfaces:
              filters?.driveFilter.driveInterface?.length > 0 ? filters?.driveFilter.driveInterface : undefined,
            nominalPower: filters?.driveFilter.nominalPower?.length > 0 ? filters?.driveFilter.nominalPower : undefined,
            switchingFreq:
              filters?.driveFilter.switchingFreq?.length > 0 ? filters?.driveFilter.switchingFreq : undefined,
            format: filters?.driveFilter.format?.length > 0 ? filters?.driveFilter.format : undefined,
            nbPhases:
              formatNbPhases(filters?.driveFilter.nbPhases)?.length > 0
                ? formatNbPhases(filters?.driveFilter.nbPhases)
                : undefined,
            outputVoltages:
              filters?.driveFilter?.driveVoltages.length > 0 ? filters?.driveFilter.driveVoltages : undefined
          },
          gearboxFilter: {
            manufacturer: undefined,
            type: filters?.gearboxFilter.gearboxType?.length > 0 ? filters?.gearboxFilter.gearboxType : undefined,
            precision: undefined,
            reductionRatio: filters?.gearboxFilter.ratio.length > 0 ? filters?.gearboxFilter.ratio : undefined,
            specification: undefined,
            efficiency: filters?.gearboxFilter.efficiency?.length > 0 ? filters?.gearboxFilter.efficiency : undefined,
            productOrComponentType:
              filters?.gearboxFilter.productOrComponentType?.length > 0
                ? filters?.gearboxFilter.productOrComponentType
                : undefined,
            torsionalBacklash: filters?.gearboxFilter?.torsionalBacklash,
            inputDiameter:
              filters?.gearboxFilter.inputDiameter?.length > 0 ? filters?.gearboxFilter.inputDiameter : undefined,
            externalDiameter:
              filters?.gearboxFilter.externalDiameter?.length > 0 ? filters?.gearboxFilter.externalDiameter : undefined
          }
        },
        selectedAxis: selectedAxis?.axis?.axisGCR,
        selectedAxisInertia: selectedAxis?.calculations?.totalAxisInertiaPreCalculations.preCalcTranslationalInertia
      };
      // console.log("payload", payload);
      let bdls = {
        paginatedBundles: [],
        countOfResults: 0
      };
      try {
        bdls = await getBundles(payload);
        // check if payload is different than fetchBundlesPayload
        if (configurationType === "RETROFIT") {
          bdls.paginatedBundles = await processBundles(bdls.paginatedBundles);
        } else if (configurationType === "CARTESIAN") {
          bdls.paginatedBundles = await processCartesianBundles(bdls.paginatedBundles);
        }
        return bdls.paginatedBundles;
      } catch (error) {
        console.error(error.response?.status);
      } finally {
        dispatch(setFetchedBundles({ ...bdls, fetchBundlesPayload: payload }));
        dispatch(
          setNominalTorqueUserInput({
            min: Math.min(
              ...(
                bdls.paginatedBundles || [Number(((filters?.applicationDataFilter?.nominalTorque || 1) * 3).toFixed(2))]
              ).map((bundle) => Number(parseFloat(bundle.nominalTorque).toFixed(2)))
            ),
            max: Math.max(
              ...(
                bdls.paginatedBundles || [Number(((filters?.applicationDataFilter?.nominalTorque || 1) * 3).toFixed(2))]
              ).map((bundle) => Number(parseFloat(bundle.nominalTorque).toFixed(2)))
            )
          })
        );
        dispatch(
          setInertiaRatioUserInput({
            min: 0,
            max: 10
          })
        );
        dispatch(setPaging({ skip: 0, limit: rowsToShow }));
        dispatch(
          setOrderBy({
            attributeName: "nominalTorque",
            sort: "desc"
          })
        );
      }
      return undefined;
    },
    {
      enabled: false
    }
  );

  // check drive filters function
  const checkDriveFilters = () => {
    return (
      filters?.driveFilter?.driveInterface?.length > 0 &&
      filters?.driveFilter?.driveInterface?.[0] !== "" &&
      filters?.driveFilter?.nbPhases?.length > 0 &&
      filters?.driveFilter?.nbPhases?.[0] !== "" &&
      filters?.driveFilter?.driveVoltages?.length > 0 &&
      filters?.driveFilter?.driveVoltages?.[0] !== ""
    );
  };

  // check motor filters function
  const checkMotorFilters = () => {
    return (
      filters?.motorFilter?.ipDegreeOfProtection?.length > 0 &&
      filters?.motorFilter?.holdingBrake?.length > 0 &&
      (!(filters?.motorFilter?.withGearbox || filters?.gearboxFilter?.enabled) ||
        filters?.gearboxFilter?.productOrComponentType?.length > 0)
    );
  };

  // check gearbox filters function
  const checkGearboxFilters = () => {
    return (
      !(filters?.motorFilter?.withGearbox || filters?.gearboxFilter?.enabled) ||
      filters?.gearboxFilter?.productOrComponentType?.length > 0
    );
  };

  return {
    isFetching: isFetching || isLoading,
    refetch: fetchRetrofitBundles
  };
};
